import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createTestimonialDto,
  QueryDto,
  Testimonial,
  updateTestimonialDto,
} from "./types";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_CUSTOMER_APP_BACKEND_PREFIX,
]
  .filter(Boolean)
  .join("");

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("Warning: REACT_APP_API_BASE_URL is not defined.");
}
if (!process.env.REACT_APP_CUSTOMER_APP_BACKEND_PREFIX) {
  console.warn(
    "Warning: REACT_APP_CUSTOMER_APP_BACKEND_PREFIX is not defined.",
  );
}

// Axios instance with cookies support
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token from cookies
api.interceptors.request.use(
  async (config) => {
    let token = getCookie("access_token");
    if (token === null || token === undefined) {
      try {
        token = getCookie("access_token");
      } catch (err) {
        // Handle refresh failure (e.g., redirect to login)
      }
    }
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export const fetchTestimonials = createAsyncThunk(
  "testimonials/fetchTestimonials",
  async (query: QueryDto, { rejectWithValue }) => {
    try {
      const params: Record<string, any> = {};
      if (query.page) params.page = query.page;
      if (query.limit) params.limit = query.limit;
      if (query.status) params.status = query.status;
      const response = await api.get(`/customer/testimonials`, {
        params,
      });
      // console.log(response.data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const createTestimonial = createAsyncThunk(
  "testimonials/createTestimonial",
  async (testimonialData: createTestimonialDto, { rejectWithValue }) => {
    try {
      const response = await api.post(`/customer/testimonials`, testimonialData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  },
);

export const fetchTestimonialById = createAsyncThunk(
  "testimonials/fetchTestimonialById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/customer/testimonials/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const updateTestimonial = createAsyncThunk(
  "testimonials/updateTestimonial",
  async (
    { id, testimonial }: { id: string; testimonial: updateTestimonialDto },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.patch(
        `/customer/testimonials/${id}`,
        testimonial,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const deleteTestimonial = createAsyncThunk(
  "testimonials/deleteTestimonial",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/customer/testimonials/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

interface TestimonialsState {
  testimonials: {
    data: Testimonial[];
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
  currentTestimonial: Testimonial | null;
  isLoading: boolean;
  error: string | null;
  success: boolean;
}

const initialState: TestimonialsState = {
  testimonials: {
    data: [],
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
  },
  currentTestimonial: null,
  isLoading: false,
  error: null,
  success: false,
};

const testimonialsSlice = createSlice({
  name: "testimonials",
  initialState,
  reducers: {
    clearError: (state: TestimonialsState) => {
      state.error = null;
    },
    clearSuccess: (state: TestimonialsState) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTestimonials.pending, (state: TestimonialsState) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(
        fetchTestimonials.fulfilled,
        (state: TestimonialsState, action: PayloadAction<Testimonial[]>) => {
          state.isLoading = false;
          state.testimonials.data = action.payload;
          state.success = true;
        },
      )
      .addCase(
        fetchTestimonials.rejected,
        (state: TestimonialsState, action: PayloadAction<unknown>) => {
          state.isLoading = false;
          // state.error = action.payload as string;
          state.success = false;
        },
      )
      .addCase(createTestimonial.pending, (state: TestimonialsState) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(
        createTestimonial.fulfilled,
        (state: TestimonialsState, action: PayloadAction<Testimonial>) => {
          state.isLoading = false;
          state.testimonials.data.unshift(action.payload);
          state.success = true;
        },
      )
      .addCase(
        createTestimonial.rejected,
        (state: TestimonialsState, action: PayloadAction<unknown>) => {
          state.isLoading = false;
          state.error = action.payload as string;
          state.success = false;
        },
      )
      .addCase(fetchTestimonialById.pending, (state: TestimonialsState) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(
        fetchTestimonialById.fulfilled,
        (state: TestimonialsState, action: PayloadAction<Testimonial>) => {
          state.isLoading = false;
          state.currentTestimonial = action.payload;
          state.success = true;
        },
      )
      .addCase(
        fetchTestimonialById.rejected,
        (state: TestimonialsState, action: PayloadAction<unknown>) => {
          state.isLoading = false;
          state.error = action.payload as string;
          state.success = false;
        },
      )
      .addCase(updateTestimonial.pending, (state: TestimonialsState) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(
        updateTestimonial.fulfilled,
        (state: TestimonialsState, action: PayloadAction<Testimonial>) => {
          state.isLoading = false;
          state.currentTestimonial = action.payload;
          state.success = true;
        },
      )
      .addCase(
        updateTestimonial.rejected,
        (state: TestimonialsState, action: PayloadAction<unknown>) => {
          state.isLoading = false;
          state.error = action.payload as string;
          state.success = false;
        },
      )
      .addCase(deleteTestimonial.pending, (state: TestimonialsState) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(
        deleteTestimonial.fulfilled,
        (state: TestimonialsState, action: PayloadAction<Testimonial>) => {
          state.isLoading = false;
          state.testimonials.data = state.testimonials.data.filter(
            (testimonial) => testimonial._id !== action.payload._id,
          );
          state.success = true;
        },
      )
      .addCase(
        deleteTestimonial.rejected,
        (state: TestimonialsState, action: PayloadAction<unknown>) => {
          state.isLoading = false;
          state.error = action.payload as string;
          state.success = false;
        },
      );
  },
});

export const { clearError, clearSuccess } = testimonialsSlice.actions;
export default testimonialsSlice.reducer;
